import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Tag, Button, Form, Input, Space, message, Row, Col, Descriptions, Empty, Tooltip } from 'antd';
import { ReloadOutlined, SendOutlined, ThunderboltOutlined, SaveOutlined } from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import UserNotifications from '../system/UserNotifications';

const LEVEL = {
  critical: { color: 'red', label: '严重' },
  warning: { color: 'orange', label: '警告' },
  resolved: { color: 'green', label: '恢复/测试' },
};

const fmt = (ts) => (ts ? new Date(ts).toLocaleString() : '-');

function summarizeFast(r) {
  if (!r) return '尚未运行';
  if (r.error) return `检测出错：${r.error}`;
  const parts = [];
  if (r.offline?.length) parts.push(`离线 ${r.offline.join('、')}`);
  if (r.error_rate?.length) parts.push(`错误率高 ${r.error_rate.join('、')}`);
  if (r.no_logs?.length) parts.push(`日志缺失 ${r.no_logs.join('、')}`);
  return parts.length ? parts.join('；') : '全部正常';
}

function summarizeCerts(r) {
  if (!r) return '尚未运行';
  return r.length ? `${r.length} 项异常：${r.slice(0, 3).join('；')}${r.length > 3 ? '…' : ''}` : '全部正常';
}

export default function OpsAlerts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    const res = await cdnService.getOpsAlerts();
    if (res.success) {
      setData(res.data);
      form.setFieldsValue(res.data.config || {});
    } else {
      message.error(res.error || '加载失败');
    }
    setLoading(false);
  }, [form]);

  useEffect(() => { load(); }, [load]);

  const runCheck = async () => {
    setChecking(true);
    const res = await cdnService.runOpsCheck();
    setChecking(false);
    if (res.success) {
      message.success(`检测完成：${summarizeFast(res.data)}`);
      load();
    } else message.error(res.error || '检测失败');
  };

  const save = async () => {
    const v = await form.validateFields();
    setSaving(true);
    const res = await cdnService.saveOpsAlertChannels(v);
    setSaving(false);
    if (res.success) {
      message.success('已保存');
      load();
    } else message.error(res.error || '保存失败');
  };

  const test = async () => {
    setTesting(true);
    const res = await cdnService.testOpsAlert();
    setTesting(false);
    if (res.success) message.success('测试消息已发送，请查收');
    else message.error(res.error || res.message || '发送失败');
  };

  const last = data?.last_checks || {};
  const channels = data?.channels || [];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        title="巡检状态"
        extra={
          <Space>
            <Tooltip title="刷新"><Button icon={<ReloadOutlined />} onClick={load} loading={loading} /></Tooltip>
            <Button icon={<ThunderboltOutlined />} onClick={runCheck} loading={checking}>立即检测</Button>
          </Space>
        }
      >
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="节点 / 错误率 / 日志（每 5 分钟）">
            {summarizeFast(last.fast?.result)}
            <span style={{ color: '#999', marginLeft: 8 }}>{fmt(last.fast?.ts)}</span>
          </Descriptions.Item>
          <Descriptions.Item label="节点证书（每小时）">
            {summarizeCerts(last.certs?.result)}
            <span style={{ color: '#999', marginLeft: 8 }}>{fmt(last.certs?.ts)}</span>
          </Descriptions.Item>
          <Descriptions.Item label="告警渠道">
            {channels.length ? channels.map((c) => <Tag key={c}>{c}</Tag>) : <Tag color="orange">未配置（告警只记录在下方列表）</Tag>}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Row gutter={16}>
        <Col xs={24} lg={15}>
          <Card title="最近告警" bodyStyle={{ padding: 0 }}>
            <Table
              rowKey={(r, i) => `${r.ts}-${i}`}
              size="small"
              loading={loading}
              dataSource={data?.alerts || []}
              locale={{ emptyText: <Empty description="暂无告警" /> }}
              pagination={{ pageSize: 20, size: 'small' }}
              columns={[
                { title: '时间', dataIndex: 'ts', width: 170, render: fmt },
                {
                  title: '级别', dataIndex: 'level', width: 90,
                  render: (l) => <Tag color={(LEVEL[l] || {}).color}>{(LEVEL[l] || { label: l }).label}</Tag>,
                },
                {
                  title: '内容',
                  render: (_, r) => (
                    <div>
                      <div style={{ fontWeight: 500 }}>{r.title}</div>
                      <div style={{ color: '#888', fontSize: 12, whiteSpace: 'pre-wrap' }}>{r.text}</div>
                    </div>
                  ),
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={9}>
          <Card
            title="告警渠道"
            extra={<Button size="small" icon={<SendOutlined />} onClick={test} loading={testing} disabled={!channels.length}>发送测试</Button>}
          >
            <Form form={form} layout="vertical">
              <Form.Item name="telegram_bot_token" label="Telegram Bot Token">
                <Input.Password placeholder="123456:ABC..." autoComplete="new-password" />
              </Form.Item>
              <Form.Item name="telegram_chat_id" label="告警群 Chat ID">
                <Input placeholder="-100123456789" />
              </Form.Item>
              <Form.Item name="telegram_report_chat_id" label="统计报告群 Chat ID" tooltip="每小时运营统计">
                <Input placeholder="-100123456789" />
              </Form.Item>
              <Form.Item name="telegram_events_chat_id" label="业务事件群 Chat ID" tooltip="注册 / 下单 / 收款 / 开通 / 添加域名；留空 = 统计报告群">
                <Input placeholder="留空 = 统计报告群" />
              </Form.Item>
              <Form.Item name="webhook_url" label="Webhook 地址" tooltip="POST JSON：level / title / text">
                <Input placeholder="https://..." />
              </Form.Item>
              <Form.Item name="email_to" label="邮箱" tooltip="多个用逗号分隔；需系统邮件已配置">
                <Input placeholder="ops@example.com" />
              </Form.Item>
              <Button type="primary" icon={<SaveOutlined />} onClick={save} loading={saving} block>保存</Button>
            </Form>
          </Card>
        </Col>
      </Row>

      <UserNotifications />
    </Space>
  );
}
